import {
  AHButton,
  AHIcon,
  AHInput,
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useRadiusSize,
  SPImageSource,
} from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Platform, Switch } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSizeClass } from "../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { useKeyboardInModal } from "../../Files/useKeyboardInModal";
import { InputSectionContainer } from "../InputSectionContainer";
import { EAN13Checksum, EAN8Checksum } from "../PLUProductEdit/validPLUProduct";
import { PLUProductImageSection } from "../PLUProductCreate/PLUProductImageSection";
import { CategoryOption } from "./CategoryOption";
import { CameraView, Camera } from "expo-camera";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
type Props = {
  active: boolean;
  setActive(active: boolean): void;
  image: SPImageSource | null;
  setImage(image: { id: string; uri: string } | null): void;
  title: string;
  setTitle(title: string): void;
  eanCode: string;
  setEanCode(eanCode: string, type: "EAN" | "CODE39" | "CODE128"): void;
  pluCode: string;
  setPluCode(pluCode: string): void;
  selectedCategory: { id: string; title: string; color: string } | null;
  setSelectedCategory(categoryId: string): void;
  categories: readonly { id: string; title: string; color: string }[];
  onDelete?(): void;
  isDeleting?: boolean;
};

export function PLUProductComponent(props: Props) {
  const [keyboardBottomInset, { onLayout }] = useKeyboardInModal();
  const intl = useIntl();
  const insets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  return (
    <Box
      style={{ flex: 1, paddingBottom: keyboardBottomInset }}
      onLayout={onLayout}
    >
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: layout.space.medium,
          paddingBottom: isCompact
            ? insets.bottom + layout.space.medium
            : layout.space.medium,
        }}
      >
        <PLUProductImageSection image={props.image} setImage={props.setImage} />
        <Box spaceTop="medium" />
        <InputSectionContainer title={intl_tokens.Title}>
          <AHInput
            value={props.title}
            onChangeText={props.setTitle}
            placeholder={intl_tokens.Title}
            color="surface"
            autoCapitalize="sentences"
            textType="title"
            spaceHorizontal="medium"
            spaceVertical="small"
            cornerRadius="medium"
          />
        </InputSectionContainer>
        <Box spaceTop="medium" />
        <InputSectionContainer title="PLU">
          <AHInput
            value={props.pluCode}
            onChangeText={props.setPluCode}
            placeholder="3045"
            maxLength={6}
            keyboardType="numeric"
            color="surface"
            textType="title"
            spaceHorizontal="medium"
            spaceVertical="small"
            cornerRadius="medium"
          />
        </InputSectionContainer>
        <Box spaceTop="medium" />
        <BarCodeInput eanCode={props.eanCode} setEanCode={props.setEanCode} />
        <Box spaceTop="medium" />
        <Box
          style={{ flexDirection: "row", alignItems: "center" }}
          spaceHorizontal="medium"
          spaceVertical="small"
          color="surface"
          cornerRadius="medium"
        >
          <Box style={{ flex: 1 }} spaceRight="small">
            <AHText type="title-small" style={{ flex: 1 }}>
              <FormattedMessage
                defaultMessage="Hide product"
                description="PLUProductComponent hide product title"
              />
            </AHText>
            <AHText spaceTop="xxsmall">
              <FormattedMessage
                defaultMessage="A hidden product is only visible in edit mode."
                description="PLUProductComponent hide product description"
              />
            </AHText>
          </Box>
          <Switch
            onValueChange={(active) => props.setActive(!active)}
            value={!props.active}
          />
        </Box>
        <Box spaceTop="medium" />
        <InputSectionContainer title={intl_tokens.Category}>
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
            }}
            spaceHorizontal="medium"
            spaceVertical="small"
          >
            {props.categories.map((category) => {
              return (
                <CategoryOption
                  key={category.id}
                  category={category}
                  onPress={() => {
                    props.setSelectedCategory(category.id);
                  }}
                  selected={category.id === props.selectedCategory?.id}
                />
              );
            })}
          </Box>
        </InputSectionContainer>
        {props.onDelete ? (
          <>
            <Box spaceVertical="xxlarge" />
            <Box color="surface" cornerRadius="medium" space="medium">
              <AHButton
                themeType="negative"
                title={intl.formatMessage({
                  defaultMessage: "Remove product",
                  description:
                    "PLUProductComponent remove product button title",
                })}
                onPress={props.onDelete}
                loading={props.isDeleting}
              />
            </Box>
          </>
        ) : null}
      </KeyboardAwareScrollView>
    </Box>
  );
}

function BarCodeInput(props: {
  eanCode: string;
  setEanCode: (val: string, type: "EAN" | "CODE39" | "CODE128") => void;
}) {
  const [isScannerMode, setScannerMode] = useState(false);
  const intl = useIntl();

  const activateScannerMode = useCallback(async () => {
    const { status } = await Camera.requestCameraPermissionsAsync();
    if (status === "granted") {
      setScannerMode(true);
    }
  }, []);
  const deactivateScannerMode = useCallback(async () => {
    setScannerMode(false);
  }, []);
  const eanErrorMessage = intl.formatMessage({
    defaultMessage: "Use scanner",
    description: "PLUProductComponent barcode error message",
  });

  const eanStatus: { error: string | undefined; valid: boolean } =
    useMemo(() => {
      if (props.eanCode.length === 0) {
        return {
          error: undefined,
          valid: true,
        };
      }

      if (props.eanCode.length === 8) {
        if (EAN8Checksum(props.eanCode) === props.eanCode[7]) {
          return {
            error: undefined,
            valid: true,
          };
        } else {
          return {
            error: eanErrorMessage,
            valid: false,
          };
        }
      }

      if (props.eanCode.length === 13) {
        if (EAN13Checksum(props.eanCode) === props.eanCode[12]) {
          return {
            error: undefined,
            valid: true,
          };
        } else {
          return {
            error: eanErrorMessage,
            valid: false,
          };
        }
      }

      return {
        error: undefined,
        valid: false,
      };
    }, [eanErrorMessage, props.eanCode]);

  const topRightElement = useMemo(() => {
    if (eanStatus.valid && props.eanCode !== "") {
      return (
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
            height: 30,
          }}
        >
          <AHIcon type="check" color="positive" spaceRight="medium" />
        </Box>
      );
    }
    return (
      <TouchableBox
        style={{
          flexDirection: "row",
          alignItems: "center",
          height: 30,
        }}
        onPress={activateScannerMode}
      >
        <AHIcon
          type="scan-barcode"
          size="medium"
          color="primary"
          spaceRight="medium"
        />
      </TouchableBox>
    );
  }, [activateScannerMode, eanStatus.valid, props.eanCode]);

  const cr = useRadiusSize("medium");

  if (isScannerMode) {
    return (
      <CameraView
        barcodeScannerSettings={{
          barcodeTypes: ["ean13", "ean8", "code39", "code128"],
        }}
        onBarcodeScanned={(val) => {
          setScannerMode(false);
          // Workaround tills SDK 52
          // https://github.com/expo/expo/pull/29421
          const type = translateBarcodeType(val.type);
          props.setEanCode(
            val.data,
            type === "code39"
              ? "CODE39"
              : type === "code128"
                ? "CODE128"
                : "EAN"
          );
        }}
        style={{ height: 150, overflow: "hidden", borderRadius: cr }}
      >
        <Box
          spaceVertical="small"
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          <AHText
            type="title-small"
            spaceHorizontal="medium"
            color="lightText"
            style={{ flex: 1 }}
          >
            <FormattedMessage
              description="PLUProductComponent - barcode"
              defaultMessage="Barcode"
            />
          </AHText>
          <TouchableBox
            style={{
              flexDirection: "row",
              alignItems: "center",
              height: 30,
            }}
            spaceHorizontal="small"
            onPress={deactivateScannerMode}
          >
            <AHIcon type="edit" color="primary" spaceRight="medium" />
          </TouchableBox>
        </Box>
        <Box>
          <Box
            style={{
              height: 50,
              width: 100,
              borderColor: "red",
              borderWidth: 3,
              alignSelf: "center",
              position: "absolute",
              borderRadius: 10,
            }}
          />
        </Box>
      </CameraView>
    );
  }
  return (
    <InputSectionContainer
      title={intl.formatMessage({
        description: "PLUProductComponent - barcode",
        defaultMessage: "Barcode",
      })}
      topRightElement={topRightElement}
    >
      <AHInput
        value={props.eanCode}
        onChangeText={(e) => {
          props.setEanCode(e, "EAN");
        }}
        placeholder="0123456789123"
        keyboardType="numeric"
        color="surface"
        textType="title"
        spaceHorizontal="medium"
        spaceVertical="small"
        cornerRadius="medium"
        maxLength={13}
      />
      <Box
        style={{ flexDirection: "row", alignItems: "center" }}
        spaceHorizontal="medium"
        spaceBottom="small"
      >
        {eanStatus.error ? (
          <>
            <AHIcon type="issue" color="negative" size="small" />
            <AHText type="subtitle" color="negative" spaceLeft="xsmall">
              {eanStatus.error}
            </AHText>
          </>
        ) : (
          <AHText type="subtitle"> </AHText>
        )}
      </Box>
    </InputSectionContainer>
  );
}

function translateBarcodeType(type: number | string): string | undefined {
  if (Platform.OS === "android" && typeof type === "number") {
    // https://developers.google.com/android/reference/com/google/mlkit/vision/barcode/common/Barcode#constants
    switch (type) {
      case 1:
        return "code128";
      case 2:
        return "code39";
      case 32:
        return "ean13";
      case 64:
        return "ean8";
      default:
        return undefined;
    }
  } else if (Platform.OS === "ios") {
    switch (type) {
      case "org.iso.Code39":
      case "org.iso.Code39Mod43":
        return "code39";
      case "org.iso.Code128":
        return "code128";
      case "org.gs1.EAN-13":
        return "ean13";
      case "org.gs1.EAN-8":
        return "ean8";
    }
  }

  if (typeof type === "string") return type;
}
